import {
  Box,
  Button,
  Container,
  Heading,
  keyframes,
  Text,
  useColorModeValue,
  theme,
  usePrefersReducedMotion,
  Flex,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context";
import { ReactComponent as Logo } from "./tnc-logo.svg";

const borderGrow = keyframes`
  from {
    border-width: 10px;
  }
  to {
    border-width: 20px;
  }
`;

function Home() {
  const { isAuthenticated } = useAuth();
  const herobgColor = useColorModeValue("white", "base.700");
  const prefersReducedMotion = usePrefersReducedMotion();
  const boxbgColor = [
    "--chakra-colors-base-300a",
    useColorModeValue("--chakra-colors-white", "--chakra-colors-base-700"),
    "--chakra-colors-primary-200a",
    "--chakra-colors-transparent",
    "--chakra-colors-whiteAlpha-200",
    "--chakra-colors-primary-300a",
    "--chakra-colors-secondary-200a",
    "--chakra-colors-whiteAlpha-300",
    "--chakra-colors-secondary-300a",
  ];

  return (
    <Box
      overflow="hidden"
      border="10px solid"
      borderWidth={["8px", null, null, "10px"]}
      borderColor={herobgColor}
      height="calc(100vh - 3.5rem)"
    >
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
        gridTemplateRows="repeat(auto-fill, minmax(120px, 1fr))"
        bgImage="/images/will-turner-W8W81BsgxX4-unsplash.jpg"
        bgSize={["contain", null, "auto"]}
        bgPos={["bottom", null, "25% 50%"]}
        w="100%"
        position="relative"
        h="110%"
        overflow="hidden"
        alignContent="flex-start"
        borderRadius="md"
      >
        {Array.from(Array(450)).map((i, ind) => {
          const colorShift = keyframes`
          0% {
            background-color: var(${
    boxbgColor[ind % Math.floor(Math.random() * boxbgColor.length)]
    });
          }
          50% {
            background-color: var(${
    boxbgColor[ind % Math.floor(Math.random() * boxbgColor.length)]
    });
          }
          100% {
            background-color: var(${
    boxbgColor[ind % Math.floor(Math.random() * boxbgColor.length)]
    });
          }
        `;
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${ind}-key`}
              bg={
                prefersReducedMotion
                  ? `var(${
                    boxbgColor[
                      ind % Math.floor(Math.random() * boxbgColor.length)
                    ]
                  })`
                  : "transparent"
              }
              border="10px solid"
              borderWidth={["4px", null, null, "10px"]}
              animation={
                prefersReducedMotion
                  ? undefined
                  : `2s ease-in-out ${
                    ind % Math.floor(Math.random() * boxbgColor.length)
                  }s 1 forwards ${colorShift}, ${borderGrow} 1s ease-in-out alternate 2`
              }
              borderColor={herobgColor}
            />
          );
        })}
      </Box>
      <Container
        background={`linear-gradient(130deg, ${useColorModeValue(
          "white",
          "var(--chakra-colors-base-700)"
        )} 55%, transparent)`}
        border={`1px solid ${useColorModeValue(
          "var(--chakra-colors-primary-100a)",
          theme.colors.whiteAlpha["200"]
        )}`}
        borderRadius="lg"
        position="absolute"
        top={["5rem", null, "16vh"]}
        mx={[2, null, 12]}
        maxW={["fit-content", null, "container.sm", "container.md"]}
        px={[6, null, 8]}
        pt={[10, null, 8]}
        pb={[4, null, 6]}
        display="flex"
        flexDir="column"
        alignItems="flex-start"
        gap="3"
        backdropFilter="blur(16px)"
      >
        <Heading
          as="h2"
          fontSize={["md", null, "xl"]}
          mt="-2"
          fontFamily="mono"
          fontWeight="400"
          letterSpacing="2px"
          textTransform="uppercase"
        >
          WhalesAI.org
        </Heading>
        <Heading
          as="h1"
          fontSize={["4xl", null, "6xl"]}
          fontWeight="600"
          background={`linear-gradient(125deg, ${theme.colors.teal["200"]}, ${theme.colors.cyan["400"]}, ${theme.colors.blue["700"]})`}
          sx={{
            "-webkit-background-clip": "text",
            "-webkit-text-fill-color": "transparent",
          }}
        >
          Detect whales with satellite imagery
        </Heading>
        <Text fontSize={["md", null, "lg"]}>
          The Nature Conservancy&apos;s <strong>WhalesAI.org</strong>{" "}
          application permits validation of whale sightings detected via machine
          learning models on high resolution MAXAR imagery. Using machine
          learning models on satellite imagery will improve the state of whale
          monitoring and conservation by providing fast assessment of their
          location and pattern of movement at a species level.
        </Text>
        <Text fontSize={["md",null, "lg"]}>
          Log in to upload imagery and run models, or{" "}
          <Text
            as={Link}
            to="/explore"
            color={useColorModeValue("primary.700", "primary.400")}
          >
            view detections.
          </Text>
        </Text>
        <Box
          as="a"
          href="https://www.nature.org"
          p="4"
          transition="opacity 0.24s ease-out"
          _hover={{ opacity: "0.8" }}
        >
          <Logo width="180px" fill={useColorModeValue("#182e44", "white")} />
        </Box>
        <Box display="flex" alignSelf={["stretch", "flex-start"]} flexDir={["column", "row"]} gap={2} mt={2}>
          <Button
            variant="base"
            size={["md", "xl"]}
            as="a"
            href="https://docs.whalesai.org"
          >
            Learn More
          </Button>
          <Button
            variant="primary"
            size={["md", "xl"]}
            as={Link}
            to={isAuthenticated ? "/scenes" : "/login"}
          >
            Get Started
          </Button>
        </Box>
        <Flex flexDir={["column", "row"]} gap={[1, 20]}>
          <Text
            as={Link}
            fontSize="xs"
            left="2"
            color="primary.500"
            to="/terms"
          >
            Terms of Service
          </Text>
          <Text fontSize="xs" ml="2" color="gray.500">
            Image &copy;{" "}
            <a
              href="https://unsplash.com/photos/gray-whale-on-water-W8W81BsgxX4"
              target="_blank"
              rel="noopener noreferrer"
            >
              Will Turner on Unsplash
            </a>
          </Text>
        </Flex>
      </Container>
    </Box>
  );
}

export default Home;
